/**
 * Save ONE stored asset to the user's disk — the shared half of every
 * download in Kola.
 *
 * The naming is the caller's business and the plumbing is this module's: an
 * element downloads under the name it was uploaded with (F-135), a generated
 * style-sheet page under the page it renders (F-136), but both must fetch the
 * bytes through the AUTHENTICATED channel and hand the browser a blob
 * (core/api's `fetchAssetBlob` + core/save-file's `saveBlob` — see those two
 * for why a plain `<a href>` to the asset route is not enough in the embedded
 * shell). Written once here so a new download surface cannot get that wrong.
 *
 * `nameFor` receives the fetched blob's type — the server's recorded content
 * type — because the right extension is only knowable once the bytes are in
 * hand (the client re-encodes big images as WebP on the way up).
 */
import { showToast } from "../ui/toast.js";

import { fetchAssetBlob } from "./api.js";
import { saveBlob } from "./save-file.js";

/**
 * Fetch `assetId` and save it. Failures surface as a toast and answer
 * `false` — a download is fire-and-forget for most callers, and the ones
 * that save several files in a row use the answer to stop after the first
 * failure rather than firing a toast per file.
 */
export async function downloadAsset(
  assetId: string,
  nameFor: (blobType: string) => string,
): Promise<boolean> {
  try {
    const blob = await fetchAssetBlob(assetId);
    saveBlob(blob, nameFor(blob.type));
    return true;
  } catch (error) {
    showToast(error instanceof Error ? error.message : "Download failed");
    return false;
  }
}
